import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, App, Card, Typography, Tooltip, Dropdown } from 'antd';
import { DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import {
  listRbacProjects,
  addUserToRbacProject,
  removeUserFromRbacProject,
  listGroups,
  listUsers,
  listUnassignedUsers,
  listCategories,
} from '../services/api';
import { useThemeStyles } from '../context/ThemeContext';
import PageHeader from '../components/PageHeader';
import { pageShellStyle } from '../theme';

const { Text } = Typography;

function RbacProjects() {
  const { message } = App.useApp();
  const { token, isAdmin, user } = useAuth();
  const isPlatformAdmin = isAdmin && user?.role === 'admin';
  const themeStyles = useThemeStyles();
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [blockedAssignedUserIds, setBlockedAssignedUserIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [viewUsersModalOpen, setViewUsersModalOpen] = useState(false);
  const [viewUsersProject, setViewUsersProject] = useState(null);
  const [userForm] = Form.useForm();

  const fetchProjects = async () => {
    if (!token || !isPlatformAdmin) return;
    setLoading(true);
    try {
      const res = await listRbacProjects(token);
      const data = Array.isArray(res.data) ? res.data : [];
      // Normalize to always have _id for table rowKey/actions.
      const normalized = data.map((p) => ({
        ...p,
        _id: p._id || p.rbac_id || p.project_id,
      }));
      setProjects(normalized);
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProjects = async () => {
    if (!token || !isPlatformAdmin) return;
    try {
      const res = await listRbacProjects(token);
      const data = Array.isArray(res.data) ? res.data : [];
      const normalized = data.map((p) => ({
        ...p,
        _id: p._id || p.rbac_id || p.project_id,
      }));
      setAllProjects(normalized);
    } catch (_) {
      setAllProjects([]);
    }
  };

  const fetchGroups = async () => {
    if (!token) return;
    try {
      const res = await listGroups(token);
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setGroups([]);
    }
  };

  const fetchUsers = async () => {
    if (!token) return;
    try {
      const res = await listUsers(token);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setUsers([]);
    }
  };

  const fetchAssignableUsers = async () => {
    if (!token) return;
    try {
      const res = await listUnassignedUsers(token);
      setAssignableUsers(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setAssignableUsers([]);
    }
  };

  const fetchCategories = async () => {
    if (!token || !isPlatformAdmin) return;
    try {
      const res = await listCategories(token);
      setCategories(
        Array.isArray(res.data?.roles)
          ? res.data.roles
          : (Array.isArray(res.data?.categories) ? res.data.categories : []),
      );
    } catch (_) {
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [token, isPlatformAdmin]);

  useEffect(() => {
    if (isPlatformAdmin) {
      fetchGroups();
      fetchUsers();
      fetchAssignableUsers();
      fetchAllProjects();
      fetchCategories();
    }
  }, [token, isPlatformAdmin]);

  const openAddUser = (projectId) => {
    // Debug: track which project is being used for Add user
    // eslint-disable-next-line no-console
    console.log('[RBAC] openAddUser for project', projectId);
    userForm.setFieldsValue({ user_id: undefined, project_id: projectId, role_ids: [] });
    setBlockedAssignedUserIds(new Set());
    setUserModalOpen(true);
  };

  const onAddUserSubmit = async (values) => {
    // Debug: log submitted values
    // eslint-disable-next-line no-console
    console.log('[RBAC] onAddUserSubmit values', values);
    const projectId = values.project_id;
    if (!projectId || !values.user_id) return;
    try {
      if (addingUser) return;
      if (!token) {
        message.error('You must be logged in to add a user.');
        return;
      }
      setAddingUser(true);
      // Debug: log API call details
      // eslint-disable-next-line no-console
      console.log('[RBAC] Calling addUserToRbacProject', {
        projectId,
        userId: values.user_id,
        roleIds: values.role_ids || [],
      });
      await addUserToRbacProject(token, projectId, values.user_id, values.role_ids || []);
      message.success('User added to project');
      setUserModalOpen(false);
      if (viewUsersModalOpen && viewUsersProject?._id === projectId) {
        setViewUsersProject((prev) => ({
          ...prev,
          user_ids: [...(prev?.user_ids || []), values.user_id],
        }));
      }
      fetchProjects();
      fetchAllProjects();
      fetchAssignableUsers();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to add user';
      if (String(detail).toLowerCase().includes('already assigned to another project') && values?.user_id) {
        setBlockedAssignedUserIds((prev) => {
          const next = new Set(prev);
          next.add(values.user_id);
          return next;
        });
        userForm.setFieldsValue({ user_id: undefined });
      }
      message.error(detail);
    } finally {
      setAddingUser(false);
    }
  };

  const handleAddUserClick = () => {
    // Debug: indicate button was clicked
    // eslint-disable-next-line no-console
    console.log('[RBAC] Add user button clicked');
    if (addingUser) return;
    userForm
      .validateFields()
      .then((values) => {
        // Debug: validation succeeded
        // eslint-disable-next-line no-console
        console.log('[RBAC] userForm.validateFields success', values);
        onAddUserSubmit(values);
      })
      .catch(() => {
        // validation errors are shown by the form; no extra handling needed
      });
  };

  const handleRemoveUser = async (projectId, userId) => {
    try {
      await removeUserFromRbacProject(token, projectId, userId);
      message.success('User removed from project');
      if (viewUsersModalOpen && viewUsersProject?._id === projectId) {
        setViewUsersProject((prev) => ({
          ...prev,
          user_ids: (prev?.user_ids || []).filter((id) => id !== userId),
        }));
      }
      fetchProjects();
      fetchAllProjects();
      fetchAssignableUsers();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to remove user');
    }
  };

  const openViewUsers = (record) => {
    setViewUsersProject(record);
    setViewUsersModalOpen(true);
  };

  if (!isPlatformAdmin) {
    return (
      <Card style={{ margin: 24, background: themeStyles.cardBg }}>
        <Text style={{ color: themeStyles.textSecondary }}>Admin access required.</Text>
      </Card>
    );
  }

  const columns = [
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
      render: (t) => <Text strong style={{ color: themeStyles.textPrimary }}>{t}</Text>,
    },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Group', dataIndex: 'group_id', key: 'group_id', render: (id) => groups.find((g) => g._id === id)?.name ?? id },
    {
      title: 'Admin',
      key: 'group_admin',
      render: (_, record) => {
        const group = groups.find((g) => g._id === record.group_id);
        const adminIds = group?.admin_ids || [];
        const names = adminIds.map((uid) => users.find((u) => u._id === uid)?.username ?? uid);
        return names.length ? names.join(', ') : '—';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const userCount = (record.user_ids || []).length;
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'view',
                  label: `View Users${userCount > 0 ? ` (${userCount})` : ''}`,
                  onClick: () => openViewUsers(record),
                },
                {
                  key: 'add',
                  label: 'Add User',
                  onClick: () => openAddUser(record._id),
                },
              ],
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              aria-label="Project user actions"
              icon={<MoreOutlined style={{ fontSize: 18 }} />}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          </Dropdown>
        );
      },
    },
  ];

  const viewUserColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, uid) => (
        <Text style={{ color: themeStyles.textPrimary }}>
          {users.find((u) => u._id === uid)?.username ?? uid}
        </Text>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, uid) => (
        <Tooltip title="Remove">
          <Button
            type="link"
            size="small"
            danger
            aria-label="Remove user"
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveUser(viewUsersProject?._id, uid)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={pageShellStyle}>
      <PageHeader title="Project Users" style={{ marginBottom: 4 }} />
      <Text type="secondary" style={{ display: 'block', marginTop: 0, marginBottom: 16 }}>
        Add or remove users assigned to each project.
      </Text>
      <Table
        dataSource={projects}
        columns={columns}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title={viewUsersProject?.name ? `Users — ${viewUsersProject.name}` : 'Users'}
        open={viewUsersModalOpen}
        onCancel={() => {
          setViewUsersModalOpen(false);
          setViewUsersProject(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Table
          dataSource={viewUsersProject?.user_ids || []}
          columns={viewUserColumns}
          rowKey={(uid) => uid}
          pagination={{ pageSize: 8, hideOnSinglePage: true }}
          locale={{ emptyText: 'No users assigned to this project.' }}
          size="small"
        />
      </Modal>

      <Modal title="Add user to project" open={userModalOpen} onCancel={() => setUserModalOpen(false)} footer={null}>
        <Form form={userForm} layout="vertical" onFinish={onAddUserSubmit}>
          <Form.Item name="project_id" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="user_id" label="User" rules={[{ required: true }]}>
            {(() => {
              const inAnyProject = new Set();
              allProjects.forEach((p) => (p.user_ids || []).forEach((id) => inAnyProject.add(id)));
              const options = assignableUsers
                .filter(
                  (u) =>
                    u.role === 'user' &&
                    !inAnyProject.has(u._id) &&
                    !blockedAssignedUserIds.has(u._id)
                )
                .map((u) => ({ label: u.username, value: u._id }));
              return <Select placeholder="Select user (only unassigned to any project)" options={options} />;
            })()}
          </Form.Item>
          <Form.Item
            name="role_ids"
            label="Roles"
            rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one role' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select roles for this user's file access"
              options={(categories || []).map((c) => ({ label: c.name, value: c._id }))}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => setUserModalOpen(false)}>Cancel</Button>
              <Button type="primary" onClick={handleAddUserClick} loading={addingUser} disabled={addingUser}>
                Add
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default RbacProjects;
